import { thanos26Get, thanos26Json, thanos26PostJson } from "../../api/thanos26Client";
import { tsapiUserInfo } from "../../interfaces/ITsapiUserInfo";

export type { tsapiUserInfo };

export const MESSAGE_NO_EMPLOYEE =
    "Zum hinterlegten TimeSafe-Login gehört kein TimeSafe-Mitarbeiter. Bitte an die Administration wenden.";

/**
 * Get the TimeSafe employee of the signed in user (via the TimeSafe login stored in the settings)
 * @returns The user information
 */
export async function requestEmployee(): Promise<tsapiUserInfo> {
    const response = await thanos26Get("mitarbeiter/angemeldeterbenutzer");
    return thanos26Json(response, MESSAGE_NO_EMPLOYEE);
}

/** TimeSafe project the signed in user can book on (fields as delivered by thanos26) */
export interface ITsBuchbaresProjekt {
    id: number;
    projektNr: string | null;
    bezeichnung: string | null;
    kundenID: number | null;
    kundenNr: string | null;
    kundenBezeichnung: string | null;
}

/**
 * Get the projects the signed in user can book on (TimeSafe "ProjekteLeistungserfassung":
 * team member or open for everyone, status active/internal/offered). The customers are taken from this list.
 */
export async function requestBuchbareProjekte(): Promise<ITsBuchbaresProjekt[]> {
    const response = await thanos26Get("projekteleistungserfassung");
    return thanos26Json(response);
}

/** TimeSafe time entry (only the fields used in the frontend) */
export interface ITsZeiteintrag {
    id: number;
    taetigkeitID: number;
    datum: string;
    dauerInMinuten: number;
    textExtern: string | null;
    projektID: number;
    projektNr: string | null;
    projektBezeichnung: string | null;
    taetigkeitBezeichnung: string | null;
    taetigkeitBezeichnungMitGruppen: string | null;
}

/** The latest own time entries of the signed in user (newest first) */
export async function requestLetzteZeiteintraege(anzahl = 5): Promise<ITsZeiteintrag[]> {
    const response = await thanos26Get("zeiteintraege/letzte", { anzahl: anzahl.toString() });
    return thanos26Json(response);
}

/** One day of the week overview: booked (Ist) and target (Soll) minutes */
export interface ITsWochenTag {
    datum: string; // yyyy-MM-dd
    istMinuten: number;
    sollMinuten: number | null;
    feiertag: string | null;
}

export interface ITsWoche {
    von: string;
    bis: string;
    tage: ITsWochenTag[];
    summeIstMinuten: number;
    summeSollMinuten: number | null;
    hinweis: string | null;
}

/** Week overview (Mon–Sun) of the signed in user for the week containing datum (default: current week) */
export async function requestWochenuebersicht(datum?: string): Promise<ITsWoche> {
    const response = await thanos26Get("zeiteintraege/woche", datum ? { datum } : {});
    return thanos26Json(response);
}

/** TimeSafe time and holiday balance of the signed in user (current year until today), null if none */
export interface ITsZeitFerienInfos {
    bisDatum: string;
    zeitsaldoInMinuten: number;
    zeitsaldoInHHMM: string | null;
    feriensaldoInTagen: number;
    feriensaldoFormatiert: string | null;
    istzeitInHHMM: string | null;
    sollzeitInMinuten: number | null;
    sollzeitInHHMM: string | null;
}

export async function requestZeitFerienInfos(): Promise<ITsZeitFerienInfos | null> {
    const response = await thanos26Get("zeitferieninfos/aktuell");
    return thanos26Json(response);
}

/**
 * Get the active tasks (Tätigkeiten) of a TimeSafe project
 */
export async function requestTaetigkeiten(projektId: number): Promise<any[]> {
    const response = await thanos26Get("projekttaetigkeiten", { projektID: projektId.toString(), nurAktive: "true" });
    return thanos26Json(response);
}

/**
 * Create a time entry (Zeiteintrag). Without mitarbeiterID the backend books on the signed in user.
 * @returns The created entry
 */
export async function postZeiteintrag(eintrag: unknown): Promise<any> {
    return thanos26PostJson("zeiteintraege", {}, eintrag);
}
